import React, { useState, useCallback, useEffect } from "react";

import { useAtomValue } from "../app-jotai";
import { useVoiceRecording } from "../hooks/useVoiceRecording";
import { micModeAtom } from "../hooks/useMicMode";

import "./VoiceInputButton.scss";

interface VoiceInputButtonProps {
  onTranscript: (text: string) => void;
  disabled?: boolean;
  className?: string;
  variant?: "default" | "large";
  layout?: "default" | "reverse";
  onRecordingChange?: (isRecording: boolean) => void;
  allowEmptyTranscript?: boolean;
  disablePostProcess?: boolean;
}

export const VoiceInputButton: React.FC<VoiceInputButtonProps> = ({
  onTranscript,
  disabled = false,
  className = "",
  variant = "default",
  layout = "default",
  onRecordingChange,
  allowEmptyTranscript = false,
  disablePostProcess = false,
}) => {
  const micMode = useAtomValue(micModeAtom);

  const {
    isRecording,
    waveformBars,
    isRecordingSupported,
    startRecording,
    stopRecording,
    transcribeAudio,
    cancelRecording,
  } = useVoiceRecording();

  const [isProcessing, setIsProcessing] = useState(false);

  const isActive = isRecording || isProcessing;

  useEffect(() => {
    onRecordingChange?.(isActive);
  }, [isActive, onRecordingChange]);

  const handleStart = useCallback(async () => {
    try {
      await startRecording();
    } catch (error) {
      console.error("Voice recording start error:", error);
    }
  }, [startRecording]);

  const handleConfirm = useCallback(async () => {
    try {
      setIsProcessing(true);
      const audioBlob = await stopRecording();
      const shouldPostProcess = micMode === "smart" && !disablePostProcess;
      const text = await transcribeAudio(audioBlob, "auto", shouldPostProcess);
      if (text?.trim()) {
        onTranscript(text);
      } else if (allowEmptyTranscript) {
        onTranscript("");
      }
    } catch (error) {
      console.error("Voice recording confirm error:", error);
    } finally {
      setIsProcessing(false);
    }
  }, [
    stopRecording,
    transcribeAudio,
    onTranscript,
    micMode,
    disablePostProcess,
    allowEmptyTranscript,
  ]);

  const handleCancel = useCallback(() => {
    cancelRecording();
  }, [cancelRecording]);

  if (!isRecordingSupported) {
    return null;
  }

  const showPillBar = isRecording || isProcessing;

  // ── Pill bar: recording waveform or processing state ──
  // Uses position: absolute to escape parent size constraints.
  // The wrapper keeps the mic button's footprint so flex layout doesn't shift.
  if (showPillBar) {
    const barClasses = [
      "voice-recording-bar",
      `voice-recording-bar--${variant}`,
      isProcessing && !isRecording ? "voice-recording-bar--processing" : "",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <div
        className={`voice-input-button-wrapper voice-input-button-wrapper--${layout} voice-input-button-wrapper--expanded`}
      >
        <div className={barClasses}>
          {isRecording ? (
            <>
              {layout === "reverse" ? (
                <button
                  type="button"
                  className="voice-recording-bar__cancel"
                  onClick={handleCancel}
                  aria-label="Cancel recording"
                >
                  <span className="material-symbols-outlined">close</span>
                </button>
              ) : (
                <button
                  type="button"
                  className="voice-recording-bar__confirm"
                  onClick={handleConfirm}
                  aria-label="Confirm recording"
                >
                  <span className="material-symbols-outlined">check</span>
                </button>
              )}

              <div className="voice-recording-bar__waveform">
                {(waveformBars.length > 0
                  ? waveformBars
                  : Array(12).fill(8)
                ).map((height, i) => (
                  <span
                    key={i}
                    className="voice-recording-bar__bar"
                    style={{ height: `${Math.max(8, height)}%` }}
                  />
                ))}
              </div>

              {layout === "reverse" ? (
                <button
                  type="button"
                  className="voice-recording-bar__confirm"
                  onClick={handleConfirm}
                  aria-label="Confirm recording"
                >
                  <span className="material-symbols-outlined">check</span>
                </button>
              ) : (
                <button
                  type="button"
                  className="voice-recording-bar__cancel"
                  onClick={handleCancel}
                  aria-label="Cancel recording"
                >
                  <span className="material-symbols-outlined">close</span>
                </button>
              )}
            </>
          ) : (
            <div className="voice-recording-bar__processing-content">
              <span className="voice-recording-bar__processing-text">
                Processing
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Mic button (idle) ──
  return (
    <div
      className={`voice-input-button-wrapper voice-input-button-wrapper--${layout}`}
    >
      <button
        type="button"
        className={`voice-input-button voice-input-button--${variant} ${className}`}
        onClick={handleStart}
        disabled={disabled}
        title="Click to start voice input"
        aria-label="Start voice input"
      >
        <span className="material-symbols-outlined voice-input-button__icon">
          mic
        </span>
      </button>
    </div>
  );
};
